import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Field, SearchableInput, Recap, compressLogo } from "@/pages/register/Fields";
import { PersonalStep, CredentialsStep, validatePersonal, validateCredentials } from "@/pages/register/CommonSteps";
import { ACADEMIES, REGIONS, SCHOOL_TYPES, iefsFor, regionFor } from "@/data/senegal";

export const DIR_STEPS = ["Vous", "Établissement", "Identifiants", "Confirmation"];

export function IaIefFields({ school, setSchool }) {
  return (
    <>
      <Field label="Académie (IA)" required hint="Tapez pour rechercher, ou saisissez librement.">
        <SearchableInput id="ia-list" options={ACADEMIES.map((a) => a.ia)} value={school.ia} testid="reg-ia"
          onChange={(v) => setSchool({ ia: v, ...(regionFor(v) && !school.region ? { region: regionFor(v) } : {}) })} placeholder="ex : IA de Kédougou" />
      </Field>
      <Field label="Inspection (IEF)" required hint="Tapez pour rechercher, ou saisissez librement.">
        <SearchableInput id="ief-list" options={iefsFor(school.ia)} value={school.ief} testid="reg-ief"
          onChange={(v) => setSchool({ ief: v })} placeholder="ex : IEF Kédougou" />
      </Field>
    </>
  );
}

function LogoField({ school, setSchool }) {
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Choisissez une image");
    try { setSchool({ logo_base64: await compressLogo(f) }); } catch { toast.error("Image illisible"); }
  };
  return (
    <Field label="Logo de l'école" hint="Facultatif — image PNG ou JPG" className="sm:col-span-2">
      {school.logo_base64 ? (
        <div className="flex items-center gap-3">
          <img src={school.logo_base64} alt="Logo" className="w-16 h-16 object-contain rounded-lg border border-[#E2E8F0] bg-white" />
          <button type="button" onClick={() => setSchool({ logo_base64: null })} className="text-sm text-red-600 flex items-center gap-1"><X className="w-4 h-4" /> Retirer</button>
        </div>
      ) : (
        <label className="flex items-center gap-2 h-12 px-4 rounded-md border border-dashed border-[#1F7A63]/40 text-sm text-[#1F7A63] cursor-pointer hover:bg-[#E8F2EF] transition-colors w-fit">
          <ImagePlus className="w-4 h-4" /> Ajouter un logo
          <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="reg-logo" />
        </label>
      )}
    </Field>
  );
}

function SchoolStep({ form, set }) {
  const s = form.school;
  const setSchool = (patch) => set({ school: { ...s, ...patch } });
  const num = (k) => (e) => setSchool({ [k]: e.target.value.replace(/\D/g, "") });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Nom de l'école" required className="sm:col-span-2"><Input className="h-12" value={s.nom} onChange={(e) => setSchool({ nom: e.target.value })} placeholder="ex : École élémentaire Cire Guimba" data-testid="reg-school-nom" /></Field>
      <Field label="Type d'établissement" required>
        <Select value={s.type_etablissement} onValueChange={(v) => setSchool({ type_etablissement: v })}>
          <SelectTrigger className="h-12" data-testid="reg-school-type"><SelectValue placeholder="Choisir" /></SelectTrigger>
          <SelectContent>{SCHOOL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <IaIefFields school={s} setSchool={setSchool} />
      <Field label="Région" required><SearchableInput id="region-list" options={REGIONS} value={s.region} onChange={(v) => setSchool({ region: v })} testid="reg-region" /></Field>
      <Field label="Département"><Input className="h-12" value={s.departement} onChange={(e) => setSchool({ departement: e.target.value })} data-testid="reg-departement" /></Field>
      <Field label="Commune"><Input className="h-12" value={s.commune} onChange={(e) => setSchool({ commune: e.target.value })} data-testid="reg-commune" /></Field>
      <Field label="Localité"><Input className="h-12" value={s.localite} onChange={(e) => setSchool({ localite: e.target.value })} data-testid="reg-localite" /></Field>
      <Field label="Téléphone de l'établissement"><Input className="h-12" type="tel" value={s.telephone} onChange={(e) => setSchool({ telephone: e.target.value })} data-testid="reg-school-tel" /></Field>
      <Field label="Nombre de classes" required><Input className="h-12" inputMode="numeric" value={s.nb_classes} onChange={num("nb_classes")} data-testid="reg-nb-classes" /></Field>
      <Field label="Nombre d'enseignants"><Input className="h-12" inputMode="numeric" value={s.nb_enseignants} onChange={num("nb_enseignants")} data-testid="reg-nb-enseignants" /></Field>
      <Field label="Nombre approximatif d'élèves"><Input className="h-12" inputMode="numeric" value={s.nb_eleves} onChange={num("nb_eleves")} data-testid="reg-nb-eleves" /></Field>
      <LogoField school={s} setSchool={setSchool} />
    </div>
  );
}

const typeLabel = (v) => SCHOOL_TYPES.find((t) => t.value === v)?.label;

export const directeurForm = {
  steps: DIR_STEPS,
  render(step, form, set) {
    if (step === 0) return <PersonalStep form={form} set={set} withFonction />;
    if (step === 1) return <SchoolStep form={form} set={set} />;
    if (step === 2) return <CredentialsStep form={form} set={set} withRecovery />;
    const s = form.school;
    return (
      <Recap rows={[
        ["Prénom et nom", `${form.prenom} ${form.nom}`], ["Sexe", form.sexe === "F" ? "Féminin" : "Masculin"],
        ["Téléphone", form.telephone], ["E-mail", form.email], ["Fonction", form.fonction],
        ["École", s.nom], ["Type", typeLabel(s.type_etablissement)], ["IA", s.ia], ["IEF", s.ief],
        ["Région", s.region], ["Département", s.departement], ["Commune", s.commune], ["Localité", s.localite],
        ["Classes / enseignants / élèves", `${s.nb_classes || "—"} / ${s.nb_enseignants || "—"} / ${s.nb_eleves || "—"}`],
        ["Téléphone école", s.telephone], ["Logo", s.logo_base64 ? "Ajouté" : "Aucun"],
        ["Identifiant", form.username], ["E-mail de récupération", form.recovery_email],
      ]} />
    );
  },
  validate(step, f) {
    if (step === 0) return validatePersonal(f, true);
    if (step === 1) {
      const s = f.school;
      if (s.nom.trim().length < 2) return "Indiquez le nom de l'école.";
      if (!s.type_etablissement) return "Choisissez le type d'établissement.";
      if (!s.ia?.trim() || !s.ief?.trim()) return "Renseignez l'IA et l'IEF.";
      if (!s.region?.trim()) return "Indiquez la région.";
      if (!s.nb_classes) return "Indiquez le nombre de classes.";
      return null;
    }
    if (step === 2) return validateCredentials(f);
    return null;
  },
  submitLabel: "Envoyer ma demande d'inscription",
  successText: "Votre demande d'inscription a bien été enregistrée. Elle sera examinée par l'administrateur EDUKATI. Vous pourrez vous connecter après son approbation.",
};
